import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../assets/logo2.png";
import { Popover, Transition } from "@headlessui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { FaRegUser } from "react-icons/fa";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

const drawerWidth  = 240;
const collapsedWidth = 65;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

/* AppBar sits BELOW the drawer */
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isMobile",
})(({ theme, open, isMobile }) => ({
  position: "fixed !important",
  top: 8,
  right: 8,
  zIndex: theme.zIndex.drawer - 1,
  width: isMobile
    ? "calc(100% - 16px)"
    : open
    ? `calc(100% - ${drawerWidth}px - 16px)`
    : `calc(100% - ${collapsedWidth}px - 16px)`,
  marginLeft: isMobile ? 0 : open ? drawerWidth : collapsedWidth,
  background: "transparent !important",
  boxShadow: "none !important",
  transition: theme.transitions.create(["width", "margin-left"], {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
}));

const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const NAV_COLORS = {
  store: { bg: "#E3ECF6", border: "#BBD6F5", icon: "#2F6DB4", label: "#2F6DB4", bar: "#2F6DB4" },
};

const navItems = [
  {
    label: "Medical Store",
    icon: Inventory2Icon,
    path: "/dispenser",
    activePaths: ["/dispenser"],
    color: NAV_COLORS.store,
  },
];

const drawerPaperSx = {
  border: "none",
  borderTopRightRadius: 12,
  borderBottomRightRadius: 12,
  background: "#ffffff",
  boxShadow: "6px 0 32px rgba(0,0,0,0.10)",
  overflow: "hidden",
};

function SidebarContent({ open, location, navigate, onNavClick }) {
  return (
    <>
      <div className="relative flex items-center justify-center px-3 border-b border-gray-100 mb-2">
        <img
          src={logo}
          alt="logo"
          className={`object-contain transition-all duration-300 ${open ? "w-48 h-48" : "w-10 h-10 my-3"}`}
        />
      </div>

      {open && (
        <p className="text-[10px] font-bold tracking-[1.2px] uppercase pt-2 text-gray-400 px-5 pb-1 m-0">
          Navigation
        </p>
      )}

      <nav className="py-1">
        {navItems.map((item) => {
          const isActive = item.activePaths.includes(location.pathname);
          return (
            <NavItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              isActive={isActive}
              open={open}
              onClick={() => { navigate(item.path); onNavClick?.(); }}
              color={item.color}
            />
          );
        })}
      </nav>
    </>
  );
}

export default function PortalLayout({ children }) {
  const [open, setOpen]             = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isMobile, setIsMobile]     = useState(false);
  const [loggedUser, setLoggedUser] = useState({ id: "", name: "" });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const id   = localStorage.getItem("user_id");
    const name = localStorage.getItem("user_name");
    if (id && name) setLoggedUser({ id, name });
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    Cookies.remove("RVkXIOQUNVU1RPTUVSLUFVVEhFTlRJQ0FUSU9OIMSLQ1JFVC1L=");
    toast.success("Logout Successfully");
    navigate("/login", { replace: true });
  };

  if (location.pathname === "/" || location.pathname === "/login") return children;

  const pageTitle = location.pathname.startsWith("/dispenser") ? "Medical Store" : "Dashboard";

  return (
    <div className="flex items-stretch min-h-screen bg-slate-100">
      <CssBaseline />

      {/* TOP BAR */}
      <AppBar open={open} isMobile={isMobile}>
        <div className="rounded-2xl border border-white/70 bg-white/85 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
          <Toolbar className="!min-h-[52px] sm:!min-h-[56px] !px-2 sm:!px-3">

            <IconButton
              onClick={() => isMobile ? setMobileOpen(!mobileOpen) : setOpen(!open)}
              edge="start"
              className="!mr-1 sm:!mr-2 !w-8 !h-8 sm:!w-9 sm:!h-9 !rounded-xl !bg-gray-700/[0.07] hover:!bg-gray-700/[0.14] !transition-colors"
            >
              <MenuIcon className="!text-[18px] sm:!text-[20px] !text-gray-700" />
            </IconButton>

            <div className="ml-1 sm:ml-2">
              <p className="text-[13px] sm:text-[15px] font-bold text-gray-700 m-0 leading-tight">{pageTitle}</p>
              <p className="text-[10px] sm:text-[11px] text-gray-400 m-0 hidden sm:block">
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
              </p>
            </div>

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <Popover className="relative">
                <Popover.Button
                  as="div"
                  onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                  className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-[5px] sm:py-[6px] rounded-full cursor-pointer select-none
                    bg-gradient-to-r from-[#2F6DB4] via-[#5894d9] to-white/90
                    shadow-[0_2px_12px_rgba(0,0,0,0.2)] hover:shadow-[0_4px_20px_rgba(0,0,0,0.3)] transition-shadow duration-200"
                >
                  <AccountCircleIcon className="!text-[18px] sm:!text-[20px] !text-white/90" />
                  <IoIosArrowDown
                    className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/70 transition-transform duration-300"
                    style={{ transform: isPopoverOpen ? "rotate(-180deg)" : "rotate(0deg)" }}
                  />
                </Popover.Button>

                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="opacity-0 -translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-2"
                >
                  <Popover.Panel className="absolute top-[calc(100%+10px)] right-0 z-[9999] min-w-[200px] sm:min-w-[220px] rounded-2xl overflow-hidden bg-white/[0.97] backdrop-blur-xl border border-gray-700/10 shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
                    <div className="flex items-center gap-2.5 p-3 sm:p-4 bg-gradient-to-r from-[#2F6DB4] via-[#5894d9] to-white/90">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <FaRegUser className="text-white text-sm sm:text-base" />
                      </div>
                      <div>
                        <p className="text-white text-[13px] font-bold m-0">{loggedUser.name || "Dispenser"}</p>
                        <p className="text-white/60 text-[11px] m-0">Dispenser</p>
                      </div>
                    </div>
                    <div
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 m-2.5 px-3.5 py-2.5 rounded-xl cursor-pointer
                        bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold text-[13px]
                        shadow-[0_3px_12px_rgba(239,68,68,0.3)]
                        hover:-translate-y-px hover:shadow-[0_6px_18px_rgba(239,68,68,0.4)] transition-all duration-200"
                    >
                      <PowerSettingsNewIcon className="!text-[18px]" />
                      <span>Sign Out</span>
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>
            </div>
          </Toolbar>
        </div>
      </AppBar>

      {/* SIDEBAR — MOBILE */}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ "& .MuiDrawer-paper": { ...drawerPaperSx, width: drawerWidth } }}
        >
          <SidebarContent open={true} location={location} navigate={navigate} onNavClick={() => setMobileOpen(false)} />
        </MuiDrawer>
      )}

      {/* SIDEBAR — DESKTOP */}
      {!isMobile && (
        <DesktopDrawer variant="permanent" open={open} sx={{ "& .MuiDrawer-paper": drawerPaperSx }}>
          <SidebarContent open={open} location={location} navigate={navigate} />
        </DesktopDrawer>
      )}

      {/* MAIN CONTENT */}
      <Box component="main" sx={{ flexGrow: 1, p: 0, minWidth: 0 }}>
        <DrawerHeader />
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-[calc(100vh-80px)] p-2 sm:p-4 mr-0 sm:mr-2 mb-2 rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
          {children}
        </div>
      </Box>
    </div>
  );
}

function NavItem({ icon: Icon, label, isActive, open, onClick, color }) {
  const c = color ?? { bg: "#f3f4f6", border: "#d1d5db", icon: "#6b7280", label: "#374151", bar: "#6b7280" };
  return (
    <div
      onClick={onClick}
      title={!open ? label : ""}
      className="relative flex items-center cursor-pointer rounded-xl overflow-hidden mx-2 my-[2px] px-[10px] py-[8px] border transition-all duration-200"
      style={isActive ? { background: c.bg, borderColor: c.border, boxShadow: `0 2px 8px ${c.icon}22` } : { background: "transparent", borderColor: "transparent" }}
      onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = "#f8f9fa"; e.currentTarget.style.borderColor = "#e5e7eb"; } }}
      onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "transparent"; } }}
    >
      <div
        className="flex items-center justify-center w-[32px] h-[32px] rounded-[8px] flex-shrink-0 transition-all duration-200"
        style={isActive ? { background: c.icon } : { background: "#f1f3f5", border: "1px solid #e5e7eb" }}
      >
        <Icon sx={{ fontSize: 17 }} style={{ color: isActive ? "#fff" : "#6b7280" }} />
      </div>
      <span
        className={`ml-[10px] text-[13px] font-semibold whitespace-nowrap overflow-hidden transition-all duration-300 flex-1 ${open ? "max-w-[160px] opacity-100" : "max-w-0 opacity-0"}`}
        style={{ color: isActive ? c.label : "#4b5563" }}
      >
        {label}
      </span>
      {isActive && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-[55%] rounded-l" style={{ background: c.bar }} />
      )}
    </div>
  );
}